import { useDroppable } from '@dnd-kit/core'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useEffect, useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { FETCH_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { listFolders } from '../lib/folders-api.js'
import { LOADING, TRASH_ACTION } from '../lib/labels.js'
import { getProject, projectListQueryOptions } from '../lib/projects-api.js'
import {
  DROP_TRASH,
  DROP_UNCATEGORIZED,
  type ProjectsLocation,
  parseProjectsLocation,
  projectsLocationHref,
  sameProjectsLocation,
  sidebarDropId,
} from '../lib/projects-location.js'
import { UNCATEGORIZED_NODE_KEY, groupActiveProjectsByFolder } from '../lib/sidebar-tree.js'
import { useSidebarExpanded } from '../lib/use-sidebar-expanded.js'
import { IconAllProjects, IconFolder, IconTrash, IconUncategorized } from './SidebarIcons.js'
import { type ProjectsState, SourceTreeNode } from './SidebarTreeNode.js'
import { dropHighlight, sourceRowClass } from './sidebar-row-styles.js'

type Props = {
  onNavigate?: (() => void) | undefined
}

/**
 * Finder / Google ドライブ風のソースリスト。
 *
 * フォルダ（と未分類）は開閉でき、中のプロジェクトを並べる。ファイルは出さない
 * （メイン領域のファイル一覧と二重表示になるため。ADR 0006）。
 * 一覧はメイン画面と同じクエリを共有するので、移動・作成は両方に即時反映される。
 */
export function SidebarSourceList({ onNavigate }: Props) {
  const selection = useSidebarSelection()
  const { expanded, toggle, ensureExpanded } = useSidebarExpanded()

  const foldersQ = useQuery({
    queryKey: ['folders'],
    queryFn: listFolders,
  })
  const projectsQ = useQuery(projectListQueryOptions(false))

  const grouped = useMemo(
    () => groupActiveProjectsByFolder(projectsQ.data?.items ?? []),
    [projectsQ.data],
  )
  const projectsState: ProjectsState = { isLoading: projectsQ.isLoading, error: projectsQ.error }

  // 閲覧中のプロジェクトが閉じたフォルダの中に隠れないよう、その所属を開く。
  // 開くだけで閉じないので、利用者が後から畳んだ状態は尊重される。
  useEffect(() => {
    if (selection.activeNodeKey) ensureExpanded(selection.activeNodeKey)
  }, [selection.activeNodeKey, ensureExpanded])

  if (foldersQ.isLoading) {
    return <p className="px-2 py-1.5 text-xs text-muted-foreground">{LOADING.fetching}</p>
  }

  const foldersUnavailable = !!foldersQ.error
  const folders = foldersQ.data?.items ?? []

  const treeProps = {
    selected: selection.location,
    activeProjectId: selection.activeProjectId,
    projectsState,
    onNavigate,
  }

  return (
    <div className="space-y-4">
      <SourceSection title="場所">
        <SourceLink
          location={{ kind: 'all' }}
          selected={selection.location}
          onNavigate={onNavigate}
          icon={<IconAllProjects />}
          label="すべてのプロジェクト"
        />
        {!foldersUnavailable &&
          folders.map((folder) => (
            <SourceTreeNode
              key={folder.id}
              {...treeProps}
              dropId={sidebarDropId(folder.id)}
              location={{ kind: 'folder', folderId: folder.id }}
              icon={<IconFolder />}
              label={folder.name}
              projects={grouped.byFolderId.get(folder.id) ?? []}
              isExpanded={expanded.has(folder.id)}
              onToggle={() => toggle(folder.id)}
              onExpand={() => ensureExpanded(folder.id)}
            />
          ))}
        {!foldersUnavailable && (
          <SourceTreeNode
            {...treeProps}
            dropId={sidebarDropId(DROP_UNCATEGORIZED)}
            location={{ kind: 'uncategorized' }}
            icon={<IconUncategorized />}
            label="未分類"
            projects={grouped.uncategorized}
            isExpanded={expanded.has(UNCATEGORIZED_NODE_KEY)}
            onToggle={() => toggle(UNCATEGORIZED_NODE_KEY)}
            onExpand={() => ensureExpanded(UNCATEGORIZED_NODE_KEY)}
          />
        )}
        <SourceDropLink
          dropId={sidebarDropId(DROP_TRASH)}
          location={{ kind: 'trash' }}
          selected={selection.location}
          onNavigate={onNavigate}
          icon={<IconTrash />}
          label={TRASH_ACTION.tab}
        />
      </SourceSection>

      {foldersUnavailable && (
        <p className="px-2 text-[10px] leading-relaxed text-muted-foreground">
          {toUserMessage(foldersQ.error, FETCH_ERROR_FALLBACK)}
        </p>
      )}
    </div>
  )
}

type SidebarSelection = {
  /** 一覧画面で選んでいる場所。詳細画面ではプロジェクト行を強調するので null */
  location: ProjectsLocation | null
  /** 詳細画面で開いているプロジェクト */
  activeProjectId: string | null
  /** 開いているプロジェクトの所属ノード（フォルダ ID または未分類） */
  activeNodeKey: string | null
}

function useSidebarSelection(): SidebarSelection {
  const { pathname, search } = useLocation()
  const { id: projectId } = useParams<{ id: string }>()

  const onList = pathname === '/projects'
  const onDetail =
    !!projectId &&
    (pathname === `/projects/${projectId}` || pathname.startsWith(`/projects/${projectId}/`))

  const projectQ = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => getProject(projectId as string),
    enabled: onDetail,
  })

  if (onList) {
    return {
      location: parseProjectsLocation(new URLSearchParams(search)),
      activeProjectId: null,
      activeNodeKey: null,
    }
  }
  if (onDetail) {
    const folderId = projectQ.data?.folderId
    return {
      location: null,
      activeProjectId: projectId,
      activeNodeKey:
        folderId === undefined ? null : folderId === null ? UNCATEGORIZED_NODE_KEY : folderId,
    }
  }
  return { location: null, activeProjectId: null, activeNodeKey: null }
}

function SourceSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
        {title}
      </h3>
      <ul className="space-y-0.5">{children}</ul>
    </section>
  )
}

/** 開閉トグルの幅。トグルの無い行もこの幅を空けて、ラベルの頭を揃える */
function ToggleSpacer() {
  return <span aria-hidden="true" className="w-5 shrink-0" />
}

function SourceLink({
  location,
  selected,
  onNavigate,
  icon,
  label,
}: {
  location: ProjectsLocation
  selected: ProjectsLocation | null
  onNavigate?: (() => void) | undefined
  icon: ReactNode
  label: string
}) {
  const isActive = selected !== null && sameProjectsLocation(selected, location)
  return (
    <li className="flex items-center">
      <ToggleSpacer />
      <Link
        to={projectsLocationHref(location)}
        {...(onNavigate ? { onClick: onNavigate } : {})}
        className={`${sourceRowClass(isActive)} flex-1`}
        aria-current={isActive ? 'page' : undefined}
      >
        <span className="shrink-0 text-muted-foreground">{icon}</span>
        <span className="truncate flex-1">{label}</span>
      </Link>
    </li>
  )
}

function SourceDropLink({
  dropId,
  location,
  selected,
  onNavigate,
  icon,
  label,
}: {
  dropId: string
  location: ProjectsLocation
  selected: ProjectsLocation | null
  onNavigate?: (() => void) | undefined
  icon: ReactNode
  label: string
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId })
  const isActive = selected !== null && sameProjectsLocation(selected, location)
  return (
    <li ref={setNodeRef} className="flex items-center">
      <ToggleSpacer />
      <Link
        to={projectsLocationHref(location)}
        {...(onNavigate ? { onClick: onNavigate } : {})}
        className={`${sourceRowClass(isActive)} flex-1 ${dropHighlight(isOver)}`}
        aria-current={isActive ? 'page' : undefined}
      >
        <span className="shrink-0 text-muted-foreground">{icon}</span>
        <span className="truncate flex-1">{label}</span>
      </Link>
    </li>
  )
}
